import type { UIFilter } from '@unifyapps/network/generated/models/uIFilter';
import type { BlockComponentStateId, BlockComponentType } from '../../types/block';
import type { SlotType } from '../../types/slot';
import type { StyleObject } from '../../types/styleObject';
import type { FileType } from '../FileUpload/types';
import type { EventTypeEnum } from '../../types/event';
import type { CitationType } from '../Citation/types';
import type { AIAgentEntity, Message } from '../CopilotChat/types';

export type AutomationConfig = Record<string, string> & {
  supportsStreaming?: boolean;
  type?: string;
};

export const enum AIActions {
  CreateCase = 'create_case_automation',
  FetchMessageAttachment = 'fetch_attachment_by_message_id_automation',
  FetchMessageById = 'fetch_message_by_id_automation',
  ResumeSSE = 'resume_streaming_automation',
  ClearMessageList = 'clear_conversation_automation',
  FetchCaseList = 'fetch_case_list_automation',
  RetryCase = 'retry_response_automation',
  FetchConversation = 'fetch_conversation_automation',
  FeedbackAutomation = 'feedbackAutomation',
  FetchProfileMessageAutomation = 'fetch_profile_messages_automation',
  StopResponseGeneration = 'stop_response_generation_automation',
}

type Content = {
  variant?: ChatVariants;
  welcomeText?: string;
  aiAgentId?: string;
  filters?: UIFilter;
  initialChatId?: string;
  shouldPollInfinitely?: boolean;
  maxPollDuration?: number;
  showCitationPanel?: boolean;
  showMessageActions?: boolean;
  addOns?: {
    messageTimestamp?: {
      show?: boolean;
    };
  };
  enableProfileMessages?: boolean;
};

type Appearance = {
  styles?: Pick<
    StyleObject,
    'gap' | 'height' | 'width' | 'backgroundColor' | 'borderRadius' | 'position'
  >;
  //idea: alternative to this can be mobile view or desktop view where complete mobile customization can be incorporated.
  size: 'sm' | 'lg';
  messageVariant?: 'DEFAULT' | 'BUBBLE';
  useAIAgentAvatar?: boolean;
};

type Slots = {
  messageHistory?: SlotType;
  messageInputPanel?: SlotType;
  agentDetails?: SlotType;
};

export type CopilotComponentType = BlockComponentType<'Copilot', Content, Appearance, Slots>;

export type CopilotBlockRef = {
  submitMessage?: (input: {
    message?: string;
    attachments?: FileType[];
    createCaseParams?: Record<string, unknown>;
  }) => void;
  createNewChat?: () => void;
  goToChat?: (chatId: string) => void;
  onScroll?: (event: React.SyntheticEvent) => void;
  onStopResponse?: () => void;
};

export type OnCitationClickEventState = {
  [EventTypeEnum.OnCitationClick]: {
    citationId: string | undefined;
    messageId: string;
  } & Pick<CitationType, 'chunkContents' | 'title' | 'author' | 'name' | 'referenceUrl' | 'date'>;
};

export type CopilotBlockStateType = Pick<
  CopilotComponentType,
  'appearance' | 'content' | 'componentType' | 'slots'
> &
  BlockComponentStateId & {
    chatId?: string;
    errorMessage?: string;
    automationsConfig?: AutomationConfig;
    aiAgentId?: string;
    isGeneratingResponse?: boolean;
    messages?: Message[];
    aiAgent: {
      id?: string;
      isLoading?: boolean;
      entityType?: string;
    } & AIAgentEntity['properties'];
    events?: {
      [EventTypeEnum.OnButtonClick]: {
        message: string | undefined;
      };
      [EventTypeEnum.OnMessageSent]: {
        message: string;
        attachments?: FileType[];
      };
    } & OnCitationClickEventState;
  };

export type CopilotEvents = {
  onButtonClick: (message: string) => void;
  onCitationClick: (props: OnCitationClickEventState[EventTypeEnum.OnCitationClick]) => void;
  onMessageSent: ({ message, attachments }: { message?: string; attachments?: FileType[] }) => void;
  onCaseCreated: (caseId: string) => void;
  // onMessageReceived: ({ messageId }: { messageId: string }) => void;
};

export const enum CopilotBlockMethods {
  SendMessage = 'submitMessage',
  CreateNewChat = 'createNewChat',
  GoToChat = 'goToChat',
  StopResponseGeneration = 'stopResponseGeneration',
}

export const enum ChatVariants {
  LiveChat = 'LIVE_CHAT',
  AIAgent = 'AI_AGENT',
}
